import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { AtAGlance } from './finance/AtAGlance';
import { Statements } from './finance/Statements';
import { IncomeExpenses } from './finance/IncomeExpenses';
import { Collections } from './finance/Collections';
import { Vendors } from './finance/Vendors';
import { Projects } from './finance/Projects';
import { Forecast } from './finance/Forecast';
import { BudgetPlanner } from './finance/BudgetPlanner';
import { Insights } from './finance/Insights';
import { FinanceSettings } from './finance/FinanceSettings';
import { ManualSnapshots } from './finance/ManualSnapshots';
import { PageHeader } from '../components/PageHeader';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'statements', label: 'Monthly statements' },
  { id: 'income', label: 'Income & expenses' },
  { id: 'vendors', label: 'Vendors' },
  { id: 'collections', label: 'Collections & delinquencies' },
  { id: 'projects', label: 'Special expenses & projects' },
  { id: 'forecast', label: 'Cash flow forecast' },
  { id: 'budget', label: 'Budget planner' },
  { id: 'insights', label: 'Financial insights' },
  { id: 'settings', label: 'Settings' },
] as const;

type TabId = (typeof TABS)[number]['id'];

/**
 * Board-only financial intelligence. The route and nav are already gated; this
 * is a second line of defence for anything that renders it by mistake. The
 * server enforces the same rule on every request.
 */
export function FinancialsPage() {
  const { canUseFinance, community } = useAuth();
  const [tab, setTab] = useState<TabId>('overview');

  useEffect(() => {
    if (!canUseFinance) return;
    void api.analyticsPage(`/financials/${tab}`, `Financials · ${tab}`).catch(() => undefined);
  }, [canUseFinance, tab]);

  if (!canUseFinance) return null;

  return (
    <div>
      <PageHeader
        title="Financials"
        lede={`${community?.name ? `${community.name}. ` : ''}Visible to Board Members of this community only. Figures come from approved monthly statements; anything calculated is labelled as an estimate.`}
      />

      <div className="subnav" role="tablist" aria-label="Financials sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={tab === t.id ? 'on' : ''}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {tab === 'overview' && <AtAGlance onGoto={(id) => setTab((id === 'statements' ? 'statements' : id) as TabId)} />}
        {tab === 'statements' && <Statements />}
        {tab === 'income' && <IncomeExpenses />}
        {tab === 'vendors' && <Vendors />}
        {tab === 'collections' && <Collections />}
        {tab === 'projects' && <Projects />}
        {tab === 'forecast' && <Forecast />}
        {tab === 'budget' && <BudgetPlanner />}
        {tab === 'insights' && <Insights />}
        {tab === 'settings' && (
          <>
            <FinanceSettings />
            <div style={{ marginTop: 24 }}>
              <h2 className="page-title" style={{ fontSize: 20 }}>Manual snapshots</h2>
              <ManualSnapshots />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
